import {
  Heading,
  Table,
  Tr,
  Th,
  Td,
  Link,
  Flex,
  Text,
  Tbody,
  Input,
  Textarea,
  Button,
  Box,
  useDisclosure
} from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { ConfirmDialog } from '../../components/ConfirmDialog'
import { DetailSheet } from '../../components/DetailSheet'
import { FileDropzone } from '../../components/FileInput'
import { FormField } from '../../components/FormField'
import { Icon } from '../../components/Icon'
import LoadingDots from '../../components/loadingDots'
import PreviewModal from '../../components/previewModal'
import { REVIEW_STATUS, StatusBadge } from '../../components/StatusBadge'
import { Tooltip } from '../../components/Tooltip'
import { formatDateTime, useAppToast, formatPhone } from '../../helpers'
import { EntregaDeTrabalhoService } from '../../services/EntregaDeTrabalho/EntregaDeTrabalhoService'
import { courseFormat, downloadLink, previewLink } from '../Dashboard/EntregaTrabalho/helper'
import {
  EEntregaDeTrabalhoType,
  IEntregaDeTrabalhoComplete,
  IEntregaDeTrabalhoUpload,
  viewableFormats
} from '../Dashboard/EntregaTrabalho/models'
import { MAX_FILE_SIZE_MB, MAX_TOTAL_SIZE_MB } from '../Dashboard/EntregaTrabalho/submitConsts'

export function PreviewEntregaDeTrabalho() {
  const { id } = useParams<string>()
  const service = new EntregaDeTrabalhoService()
  const toast = useAppToast()
  const [entregaDeTrabalho, setEntregaDeTrabalho] = useState<IEntregaDeTrabalhoComplete>()
  const [previewModal, setPreviewModal] = useState(false)
  const [previewData, setPreviewData] = useState<{ url: string; fileName: string; fileSize: number }>({
    url: '',
    fileName: '',
    fileSize: 0
  })
  const [form, setForm] = useState({
    professorName: '',
    grade: '',
    observations: ''
  })
  const [deleteId, setDeleteId] = useState<number>()
  const [isDeleting, setIsDeleting] = useState(false)
  const { isOpen: deleteDialogIsOpen, onOpen: deleteDialogOnOpen, onClose: deleteDialogIsClose } = useDisclosure()
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 })
  const [errors, setErrors] = useState<Record<string, string[]>>({})

  const handleDeleteUpload = async () => {
    if (!deleteId) {
      return
    }

    setIsDeleting(true)
    try {
      await service.deleteFile(deleteId)
      toast.success('Upload excluído')
      deleteDialogIsClose()
      loadEntregaDeTrabalho()
    } catch (error) {
      toast.error('Erro ao excluir o upload', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const loadEntregaDeTrabalho = async () => {
    if (!id) return

    service
      .getByPublicId(id)
      .then((response) => {
        setEntregaDeTrabalho(response)

        if (response.review) {
          setForm({
            professorName: response.review.teacher,
            grade: response.review.grade,
            observations: response.review.observations
          })
        } else {
          setForm({ ...form, professorName: response.teacher })
        }
      })
      .catch(handleLoadError)
  }

  useEffect(() => {
    loadEntregaDeTrabalho()
  }, [id])

  const handleLoadError = (error: unknown) => {
    if ((error as AxiosError)?.response?.status === 404) {
      toast.error('Entrega de trabalho não encontrada')
      return
    }

    toast.error('Erro ao carregar a entrega de trabalho', error)
  }

  const handleOpenPreview = (upload: IEntregaDeTrabalhoUpload) => {
    setPreviewData({
      fileName: upload.original_name,
      url: previewLink(upload),
      fileSize: Number(upload.size)
    })

    setPreviewModal(true)
  }

  const handleGrade = (e: React.FocusEvent<HTMLInputElement>) => {
    let value = parseFloat(e.target.value)
    if (value < 0) {
      value = 0
    } else if (value > 10) {
      value = 10
    }

    const newValue = Math.round(value * 20) / 20

    setForm({ ...form, grade: newValue.toString() })
  }

  const handleSubmit = async () => {
    const allFieldsAreFilled = Object.values(form).every((value) => value.trim() !== '')

    if (!entregaDeTrabalho) {
      return
    }

    if (!allFieldsAreFilled) {
      toast.warning('Campos obrigatórios', 'Preencha todos os campos para submeter a revisão.')
      return
    }

    if (isNaN(Number(form.grade))) {
      toast.warning('Nota inválida', 'A nota deve ser um número válido.')
      return
    }

    await service.review(entregaDeTrabalho.public_id, {
      teacher: form.professorName,
      observations: form.observations,
      grade: form.grade
    })

    toast.success('Trabalho revisado', 'A revisão do trabalho foi submetida')
    loadEntregaDeTrabalho()
  }

  const handleFiles = async (filesArray: File[]) => {
    setErrors({})
    if (filesArray.length === 0 || !entregaDeTrabalho) return

    setUploading(true)
    setUploadProgress({ current: 0, total: filesArray.length })

    for (let i = 0; i < filesArray.length; i++) {
      const file = filesArray[i]
      try {
        await service.uploadFile(entregaDeTrabalho?.id, file)
        setUploadProgress({ current: i + 1, total: filesArray.length })
      } catch (err) {
        const error = err as AxiosError<{ errors: Record<string, string[]> }>
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors)
        }
        toast.error('Erro no upload', `Não foi possível enviar o arquivo ${file.name}`)
      }
    }
    setUploading(false)
    loadEntregaDeTrabalho()
  }

  const handleDeleteDialog = (id: number) => {
    deleteDialogOnOpen()
    setDeleteId(id)
  }

  return !entregaDeTrabalho ? (
    <Flex bg="darkGray.500" h="100vh" align="center" justify="center" direction="column">
      <img src="/img/logo.png" alt="ABRA" style={{ margin: '0 auto' }} />
      <Text color="white">
        Carregando
        <LoadingDots />
      </Text>
    </Flex>
  ) : (
    <>
      <Flex
        bg="gray.100"
        minH="100vh"
        w="100%"
        align={{ base: 'stretch', md: 'center' }}
        justify={{ base: 'flex-start', md: 'center' }}
      >
        <Box w="100%" maxW={{ base: '100%', md: '640px' }} mx="auto" bg="white" shadow="md">
          <Box bg="darkGray.500" pt={4}>
            <img src="/img/logo.png" alt="ABRA" style={{ margin: '0 auto', display: 'block' }} />
          </Box>

          <Heading mt={4} textAlign="center">
            {EEntregaDeTrabalhoType[entregaDeTrabalho.type]}
          </Heading>
          <DetailSheet
            px={4}
            fields={[
              {
                label: 'Status da avaliação',
                value: <StatusBadge status={entregaDeTrabalho.status} statuses={REVIEW_STATUS} />
              },
              { label: 'Nome', value: entregaDeTrabalho.name },
              { label: 'CPF', value: entregaDeTrabalho.cpf },
              { label: 'Nome do Responsável', value: entregaDeTrabalho.responsable_name, emptyValue: 'Não possui' },
              { label: 'CPF do Responsável', value: entregaDeTrabalho.responsable_cpf, emptyValue: 'Não possui' },
              { label: 'E-mail', value: entregaDeTrabalho.email },
              { label: 'Telefone', value: formatPhone(entregaDeTrabalho.phone) },
              { label: 'Curso', value: entregaDeTrabalho.course },
              {
                label: 'Modalidade',
                value: courseFormat[entregaDeTrabalho.format as 'presencial' | 'online' | 'online-ao-vivo']
              },
              { label: 'Professor', value: entregaDeTrabalho.teacher },
              { label: 'Dias de Aula', value: entregaDeTrabalho['class-days'] },
              {
                label: 'Horário das Aulas',
                value: `${entregaDeTrabalho['class-hour-start'].substring(0, 5)} às ${entregaDeTrabalho[
                  'class-hour-ends'
                ].substring(0, 5)}`
              },
              { label: 'Tipo de Trabalho', value: EEntregaDeTrabalhoType[entregaDeTrabalho.type] },
              { label: 'Data da submissão', value: formatDateTime(entregaDeTrabalho.created_at) }
            ]}
          />
          <Table>
            <Tbody>
              <Tr>
                <Th colSpan={4} textAlign="center">
                  Arquivos
                </Th>
              </Tr>
              {entregaDeTrabalho.uploads.map((upload) => (
                <Tr key={upload.id}>
                  <Th>
                    {upload.original_name}{' '}
                    <Text variant="caption" as="span">
                      ({(Number(upload.size) / 1024 / 1024).toFixed(2)} Mb)
                    </Text>
                  </Th>
                  <Td>
                    {viewableFormats.includes(upload.original_name.split('.').pop()?.toLowerCase() || '') ? (
                      <Link color={'blue.500'} onClick={() => handleOpenPreview(upload)}>
                        <Icon name="lni lni-eye" label="Abrir preview" />
                      </Link>
                    ) : (
                      <Tooltip label="Formato não suportado para visualização" placement="right">
                        <Link color="blue.500" style={{ opacity: 0.4 }}>
                          <Icon name="lni lni-eye" label="Formato não suportado para visualização" />
                        </Link>
                      </Tooltip>
                    )}
                  </Td>
                  <Td>
                    <Link target="_blank" href={downloadLink(upload)} rel="noreferrer" color={'blue.500'}>
                      <Icon name="lni lni-download" label="Fazer download" />
                    </Link>
                  </Td>
                  <Td>
                    {upload.submitted_by === 'teacher' ? (
                      <Link color={'blue.500'} onClick={() => handleDeleteDialog(upload.id)}>
                        <Icon name="lni lni-trash-can" label="Excluir arquivo" />
                      </Link>
                    ) : (
                      <Tooltip
                        label="Não é possível deletar arquivos enviados por alunos"
                        aria-label="Não é possível deletar arquivos enviados por alunos"
                        placement="right"
                      >
                        <Link color={'blue.500'} opacity={'40%'}>
                          <Icon name="lni lni-trash-can" label="Não é possível deletar arquivos enviados por alunos" />
                        </Link>
                      </Tooltip>
                    )}
                  </Td>
                </Tr>
              ))}
              {/* Dropzone */}
              <Tr>
                <Td colSpan={4}>
                  {/* Estes arquivos sobem na hora, então o campo não guarda seleção: `files` fica
                      vazio e o progresso do envio é da página. A área aceitava qualquer arquivo de
                      qualquer tamanho — sem `accept` e sem validação —, e o aviso de recusa só
                      chegava do 422 do backend, um arquivo por vez. */}
                  <FileDropzone
                    accept="workFile"
                    maxSizeMB={MAX_FILE_SIZE_MB}
                    maxTotalSizeMB={MAX_TOTAL_SIZE_MB}
                    title="Adicionar arquivos: arraste e solte ou clique aqui"
                    files={[]}
                    onChange={handleFiles}
                    isDisabled={uploading}
                    error={errors?.file}
                  />
                  {uploading && (
                    <Box textAlign="center" my={2}>
                      <Text variant="caption">
                        Enviando arquivo {uploadProgress.current} de {uploadProgress.total}...
                      </Text>
                    </Box>
                  )}
                </Td>
              </Tr>
            </Tbody>
          </Table>
          <Table>
            <Tbody>
              <Tr>
                <Th textAlign="center">Avaliação/Revisão</Th>
              </Tr>
              <Tr>
                <Td>
                  <FormField label="Nome do professor" mt={2}>
                    <Input
                      size="sm"
                      readOnly={!!entregaDeTrabalho.review}
                      value={form.professorName}
                      onChange={(e) => setForm({ ...form, professorName: e.target.value })}
                    />
                  </FormField>

                  <FormField label="Nota" mt={2}>
                    <Input
                      size="sm"
                      readOnly={!!entregaDeTrabalho.review}
                      type="number"
                      step="0.05"
                      value={form.grade}
                      onChange={(e) => setForm({ ...form, grade: e.target.value })}
                      onBlur={handleGrade}
                    />
                  </FormField>
                  <FormField label="Observações" mt={2}>
                    <Textarea
                      size="sm"
                      readOnly={!!entregaDeTrabalho.review}
                      value={form.observations}
                      onChange={(e) => setForm({ ...form, observations: e.target.value })}
                    />
                  </FormField>
                  {!entregaDeTrabalho.review && (
                    <Box mt={2} textAlign="right">
                      <Button colorScheme="magenta" type="button" size="sm" onClick={handleSubmit}>
                        Validar Ficha de Término
                      </Button>
                    </Box>
                  )}
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>
      </Flex>

      <PreviewModal
        isOpen={previewModal}
        onClose={() => {
          setPreviewModal(false)
        }}
        url={previewData.url}
        fileName={previewData.fileName}
        fileSize={previewData.fileSize}
      />

      <ConfirmDialog
        isOpen={deleteDialogIsOpen}
        onClose={deleteDialogIsClose}
        onConfirm={handleDeleteUpload}
        action="delete"
        entity="upload da entrega de trabalho"
        isLoading={isDeleting}
      />
    </>
  )
}
